import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Settings as SettingsIcon, Crown, Users } from "lucide-react";

export default function Profile() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [counts, setCounts] = useState({ created: 0, favourites: 0, saved: 0 });

  useEffect(() => {
    Promise.all([
      api.get("/me/recipes").then((r) => r.data.length).catch(() => 0),
      api.get("/me/saved").then((r) => r.data.length).catch(() => 0),
      api.get("/me/bookmarks").then((r) => r.data.length).catch(() => 0),
    ]).then(([created, favourites, saved]) => setCounts({ created, favourites, saved }));
  }, []);

  if (!user) return null;
  const isPremium = !!user.is_premium;

  const stats = [
    { label: "Your recipes", value: counts.created },
    { label: "Favourites", value: counts.favourites },
    { label: "Saved", value: counts.saved },
  ];

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <div className="nerd-gradient rounded-3xl border border-black/8 bg-nerd-card p-6 sm:p-8 animate-fade-up">
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-6">
          {user.avatar_url ? (
            <img src={user.avatar_url} alt={user.name} className="w-24 h-24 rounded-2xl object-cover border border-black/15" />
          ) : (
            <div className="w-24 h-24 rounded-2xl bg-nerd-light text-white flex items-center justify-center font-display font-extrabold text-4xl">
              {(user.name || user.email).charAt(0).toUpperCase()}
            </div>
          )}

          <div className="flex-1 w-full">
            <div className="flex items-center gap-3 flex-wrap">
              <h1 className="font-display text-3xl font-extrabold tracking-tight">{user.name}</h1>
              {isPremium && <Crown className="w-5 h-5 text-nerd-light" title="Premium" />}
            </div>
            <p className="text-nerd-light/50 text-sm mt-1">{user.email}</p>
            {user.household_role === "member" && (
              <div data-testid="profile-household-badge" className="inline-flex items-center gap-2 mt-3 rounded-full bg-nerd-depth border border-black/10 px-3.5 py-1.5">
                <Users className="w-4 h-4 text-nerd-light/70" />
                <span className="text-sm text-nerd-light/80">Part of <span className="font-semibold text-nerd-light">{user.household_owner_name || "a"}’s</span> household · planner & supplies unlocked</span>
              </div>
            )}
            {user.bio && <p className="text-nerd-light/70 text-sm mt-3 max-w-lg">{user.bio}</p>}
            <div className="flex gap-6 mt-4">
              {stats.map((s) => (
                <button key={s.label} data-testid={`profile-stat-${s.label.toLowerCase().replace(/\s/g, "-")}`}
                  onClick={() => navigate("/")}
                  className="text-left hover:opacity-80 transition-opacity">
                  <span className="font-display font-bold text-xl">{s.value}</span>
                  <span className="text-nerd-light/50 text-sm ml-2">{s.label}</span>
                </button>
              ))}
            </div>
          </div>

          <button data-testid="profile-settings-btn" onClick={() => navigate("/settings")}
            className="shrink-0 inline-flex items-center gap-2 bg-nerd-depth border border-black/10 text-nerd-light font-semibold px-4 py-2.5 rounded-full text-sm hover:bg-black/5 transition-colors">
            <SettingsIcon className="w-4 h-4" /> Settings
          </button>
        </div>
      </div>
    </div>
  );
}
